import React from "react";
import { useNavigate } from "react-router-dom";
import Dialog from "@mui/material/Dialog";
import Grow from "@mui/material/Grow";
import SwipeableViews from "react-swipeable-views";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import SportsSoccerIcon from "@mui/icons-material/SportsSoccer";
import CloseIcon from "@mui/icons-material/Close";
import useScrollToTop from "../../hooks/useScrollToTop";

import { getGeneralQuestions, submitGeneralAnswer } from "../../services/goldenAwardsApi";
import constant from "../../utilities/constants.json";

import GoldenAwardCountdown from "./components/GoldenAwardCountdown";
import GoldenAwardQuestion, { getQuestionState, GoldenAwardResult } from "./components/GoldenAwardQuestion";
import GoldenAwardQuestionCard from "./components/GoldenAwardQuestionCard";
import GoldenAwardWinner from "./components/GoldenAwardWinner";
import { getAwardTheme } from "./goldenAwardTheme";

import "./mobileGoldenAwards.css";

const profileImageUrl = constant.profile_image;

// general_questions returns a flat array of raw questions — map field
// names once here so the rest of the page/components don't need to know
// about the API's _id/que_answer_option/que_answer naming.
const normalizeQuestion = (raw) => ({
  question_id: raw._id,
  question_text: raw.question,
  question_text_malayalam: raw.que_malayalam,
  answer_options: raw.que_answer_option,
  end_date: raw.end_date,
  prediction_open: raw.prediction_open,
  user_answer: raw.user_answer,
  correct_answer: raw.que_answer,
  winner_published: !!raw.winner_published,
  winners: raw.winners || [],
  is_winner: !!raw.is_winner,
});

// Builds the props GoldenAwardWinner needs from a WINNER-state question.
// `winners` is an array shape, but only ever one winner is ever selected
// per question — always take the first entry.
const getWinnerProps = (question) => {
  const winner = question.winners[0] || {};
  return {
    winnerName: winner.name,
    winnerPhoto: winner.image ? profileImageUrl + winner.image : undefined,
    isCurrentUser: question.is_winner,
  };
};

// The modal itself fades/scales in from the center of the screen — a
// centered pop, not a slide-up sheet; moving between questions inside it
// is handled by SwipeableViews instead.
const CenteredPopTransition = React.forwardRef(function CenteredPopTransition(props, ref) {
  return <Grow ref={ref} {...props} />;
});

const GoldenAwardsLoader = () => (
  <div className="goldenAwards-loader">
    <div className="goldenAwards-loader-stage">
      <SportsSoccerIcon className="goldenAwards-loader-ball" />
      <div className="goldenAwards-loader-shadow" />
    </div>
    <p className="goldenAwards-loader-text">Loading questions…</p>
  </div>
);

// A question that's LOCKED or already RESULT can't be answered/edited from
// inside the modal — show a plain read-only summary instead of the card.
const GoldenAwardModalSlide = ({ question, onAnswer }) => {
  const state = getQuestionState(question);
  const isLoggedIn = !!localStorage.webKey;

  const theme = getAwardTheme(question.question_text);

  if (state === "WINNER") {
    return <GoldenAwardWinner question={question} {...getWinnerProps(question)} />;
  }

  if (state === "LOCKED") {
    return (
      <div className="goldenAwardModalSlide-readonly">
        <div className="goldenAwardModalSlide-readonly-badge">
          {theme.image ? (
            theme.tinted ? (
              <span
                className="goldenAwardModalSlide-readonly-badgeImg goldenAwardModalSlide-readonly-badgeImg-tinted"
                style={{ WebkitMaskImage: `url(${theme.image})`, maskImage: `url(${theme.image})` }}
              />
            ) : (
              <img src={theme.image} alt="" className="goldenAwardModalSlide-readonly-badgeImg" />
            )
          ) : (
            <theme.Icon fontSize="inherit" />
          )}
        </div>
        <p className="goldenAwardModalSlide-readonly-question">{question.question_text}</p>
        <p>
          {!isLoggedIn
            ? "Time's up!"
            : question.user_answer
              ? "Your pick: " + question.user_answer
              : "You didn't answer this one"}
        </p>
      </div>
    );
  }

  if (state === "RESULT") {
    return (
      <div className="goldenAwardModalSlide-readonly">
        <div className="goldenAwardModalSlide-readonly-badge">
          {theme.image ? (
            theme.tinted ? (
              <span
                className="goldenAwardModalSlide-readonly-badgeImg goldenAwardModalSlide-readonly-badgeImg-tinted"
                style={{ WebkitMaskImage: `url(${theme.image})`, maskImage: `url(${theme.image})` }}
              />
            ) : (
              <img src={theme.image} alt="" className="goldenAwardModalSlide-readonly-badgeImg" />
            )
          ) : (
            <theme.Icon fontSize="inherit" />
          )}
        </div>
        <p className="goldenAwardModalSlide-readonly-question">{question.question_text}</p>
        <GoldenAwardResult question={question} />
      </div>
    );
  }

  return (
    <GoldenAwardQuestionCard
      question={question}
      initialAnswer={question.user_answer}
      submitLabel={question.user_answer ? "UPDATE" : "CONFIRM"}
      onAnswer={onAnswer}
    />
  );
};

const MobileGoldenAwards = () => {
  const navigate = useNavigate();
  useScrollToTop();

  const [questions, setQuestions] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [carouselIndex, setCarouselIndex] = React.useState(0);
  const [dismissable, setDismissable] = React.useState(false);
  const hasAutoOpened = React.useRef(false);

  React.useEffect(() => {
    let cancelled = false;
    getGeneralQuestions({}).then((res) => {
      if (cancelled || !res) return;
      const rawQuestions = res.data?.data || [];
      const mapped = rawQuestions.map(normalizeQuestion);
      // Questions the current user won bubble to the top, so that news is
      // the first thing they see — everything else keeps its API order
      // (Array#sort is stable).
      mapped.sort((a, b) => (b.is_winner ? 1 : 0) - (a.is_winner ? 1 : 0));
      setQuestions(mapped);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Auto-open the single modal, positioned at the first unanswered
  // question, once the questions have loaded.
  React.useEffect(() => {
    if (hasAutoOpened.current || loading) return;
    const firstPendingIndex = questions.findIndex((q) => getQuestionState(q) === "PENDING");
    if (firstPendingIndex >= 0) {
      hasAutoOpened.current = true;
      setCarouselIndex(firstPendingIndex);
      setModalOpen(true);
    }
  }, [loading, questions]);

  // The nearest upcoming end_date among questions still open, shown as the
  // page-level countdown banner (each question no longer shares one deadline).
  const openQuestions = questions.filter((q) => q.prediction_open);
  const nextDeadline =
    openQuestions.length > 0
      ? openQuestions.reduce(
          (earliest, q) => (q.end_date < earliest ? q.end_date : earliest),
          openQuestions[0].end_date,
        )
      : questions[0]?.end_date;
  const allClosed = questions.length > 0 && openQuestions.length === 0;
  // Once every question has moved to WINNER, the top-level banner should
  // switch from a countdown/expired notice to a "congratulations" callout.
  const allWinnersAnnounced =
    questions.length > 0 && questions.every((q) => getQuestionState(q) === "WINNER");

  // Submits the answer and updates state, but deliberately does NOT
  // advance the carousel / close the modal itself — that's scheduled a
  // beat later (below) so the card's own "submitted" state has time to
  // be seen before the view moves on.
  const handleAnswer = async (questionId, answer) => {
    const res = await submitGeneralAnswer(questionId, answer);
    if (!res || res.data.status !== 1) return false;

    const updatedQuestions = questions.map((question) =>
      question.question_id === questionId ? { ...question, user_answer: answer } : question,
    );
    setQuestions(updatedQuestions);

    window.setTimeout(() => {
      if (dismissable) {
        // A one-off edit — close after confirming, don't force further review.
        setModalOpen(false);
        return;
      }
      const nextPendingIndex = updatedQuestions.findIndex(
        (q) => getQuestionState(q) === "PENDING",
      );
      if (nextPendingIndex >= 0) {
        setCarouselIndex(nextPendingIndex);
      } else {
        setModalOpen(false);
      }
    }, 700);

    return true;
  };

  const handleEditRequest = (questionId) => {
    const index = questions.findIndex((q) => q.question_id === questionId);
    if (index < 0) return;
    setCarouselIndex(index);
    setDismissable(true);
    setModalOpen(true);
  };

  const goToPrev = () => setCarouselIndex((i) => Math.max(0, i - 1));
  const goToNext = () => setCarouselIndex((i) => Math.min(questions.length - 1, i + 1));

  return (
    <div className="goldenAwards">
      <div className="goldenAwards-wrapper">
        <div className="goldenAwards-top-goback">
          <div className="goldenAwards-backButton" onClick={() => navigate(-1)}>
            <div className="arrow">
              <ArrowBackIosIcon className="icon" />
            </div>
            <p>Bonus Questions</p>
          </div>
        </div>

       {(!allWinnersAnnounced && !loading) && (
          <div className="goldenAwards-header">
            <EmojiEventsIcon className="goldenAwards-header-icon" />
            <p className="goldenAwards-header-title">Predict the Bonus Questions</p>
            <div className="goldenAwards-header-underline" />
          </div>
        )}

        {loading ? (
          <GoldenAwardsLoader />
        ) : (
          <>
            {questions.length > 0 && (
              <GoldenAwardCountdown
                deadline={nextDeadline}
                isExpired={allClosed}
                winnersAnnounced={allWinnersAnnounced}
              />
            )}

            <div className="goldenAwards-questions">
              {questions.map((question) =>
                getQuestionState(question) === "WINNER" ? (
                  <GoldenAwardWinner
                    key={question.question_id}
                    question={question}
                    {...getWinnerProps(question)}
                  />
                ) : (
                  <GoldenAwardQuestion
                    key={question.question_id}
                    question={question}
                    onEditRequest={handleEditRequest}
                  />
                ),
              )}
            </div>
          </>
        )}
      </div>

      <Dialog
        open={modalOpen}
        TransitionComponent={CenteredPopTransition}
        keepMounted
        onClose={dismissable ? () => setModalOpen(false) : undefined}
        className="goldenAwardsAnswerDialog"
        aria-describedby="golden-award-answer-dialog"
      >
        <div className="goldenAwardAnswerDialog-header">
          <EmojiEventsIcon className="goldenAwardAnswerDialog-header-icon" />
          <p className="goldenAwardAnswerDialog-header-title">Bonus Questions</p>
          <p className="goldenAwardAnswerDialog-header-step">
            Question {carouselIndex + 1} of {questions.length}
          </p>
          
            <CloseIcon
              className="goldenAwardAnswerDialog-closeIcon"
              onClick={dismissable ? () => {
                setModalOpen(false);
              }:() => {
                setModalOpen(false);
                navigate(-1);
              }}
            />

        </div>
        <div className="goldenAwardAnswerDialog-body">
          <SwipeableViews index={carouselIndex} onChangeIndex={setCarouselIndex} animateTransitions>
            {questions.map((question) => (
              <div className="goldenAwardAnswerDialog-slide" key={question.question_id}>
                <GoldenAwardModalSlide
                  question={question}
                  onAnswer={(answer) => handleAnswer(question.question_id, answer)}
                />
              </div>
            ))}
          </SwipeableViews>
          <div className="goldenAwardAnswerDialog-dots">
            {questions.map((question, i) => (
              <span
                key={question.question_id}
                className={
                  i === carouselIndex
                    ? "goldenAwardAnswerDialog-dot goldenAwardAnswerDialog-dot-active"
                    : "goldenAwardAnswerDialog-dot"
                }
              />
            ))}
          </div>
        </div>

        <button
          type="button"
          className="goldenAwardAnswerDialog-navBtn goldenAwardAnswerDialog-navBtn-prev"
          disabled={carouselIndex === 0}
          onClick={goToPrev}
          aria-label="Previous question"
        >
          <ArrowBackIosNewIcon />
        </button>
        <button
          type="button"
          className="goldenAwardAnswerDialog-navBtn goldenAwardAnswerDialog-navBtn-next"
          disabled={carouselIndex === questions.length - 1}
          onClick={goToNext}
          aria-label="Next question"
        >
          <ArrowForwardIosIcon />
        </button>
      </Dialog>
    </div>
  );
};

export default MobileGoldenAwards;
